import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  PiArrowLeftDuotone,
  PiCalendarDuotone,
  PiEyeSlashDuotone,
  PiListBulletsDuotone,
  PiMagnifyingGlassDuotone,
  PiTrashDuotone,
  PiWarningDuotone,
} from 'react-icons/pi';
import { createChallenge, getChallenge, updateChallenge } from '../api/challengeService';
import { createEvent, getEvent, getEvents, searchExternalEvents } from '../api/eventService';
import { listAllEventTypes } from '../api/eventTypeService';
import { getLeague } from '../api/leagueService';
import { getRoom } from '../api/roomService';
import { accessAwareMessage } from '../api/api';
import Card from '../shared/common/Card';
import Button from '../shared/common/Button';
import Select from '../shared/common/Select';
import Breadcrumb from '../shared/common/Breadcrumb';
import Input from '../shared/common/Input';
import DateTimePicker from '../shared/common/DateTimePicker';
import EmptyState from '../shared/common/EmptyState';
import { SkeletonCard, SkeletonText } from '../shared/common/Skeleton';
import ScoringProfileBuilder, {
  DEFAULT_SCORING_PROFILE,
  normalizeScoringProfile,
  toScoringProfilePayload,
  toScoringRulesMap,
  validateScoringRules,
} from '../shared/business/scoring/ScoringProfileBuilder';
import {
  EVENT_CATEGORY_DEFAULT,
  EVENT_CATEGORY_OPTIONS,
  EVENT_CATEGORY_OTHER,
} from '../models/eventCategories';
import { readChallengePrefillSearch } from '../shared/utils/challengeCreationFlow';
import {
  isFutureEvent as isFutureEventHelper,
  findNonFutureEventIds,
  buildMultiChallengeOverrides,
  validateExternalEventSelection,
  isExternalSubmitDisabled,
  getSubmitButtonLabel,
  shouldHideBetDeadlineInput,
} from '../shared/utils/challengeCreationHelpers';
import { toDateTimeLocalValue, toUserOffsetDateTime } from '../shared/utils/dateTime';
import { generateOutcomes as generateOutcomesFromEventType } from '../shared/business/eventType/eventTypeCapabilities';
import { outcomeSchemeIcon } from '../shared/business/eventType/outcomeSchemeHelpers';
import { OutcomeScheme } from '../models/eventType';

const SCORING_PROFILE_ERROR_KEY = 'scoringProfile';
const EVENT_SOURCE_LOCAL = 'local';
const EVENT_SOURCE_EXTERNAL = 'external';
const DEFAULT_EXTERNAL_COMPETITION = 'PL';
const EVENT_LIST_FILTERS = Object.freeze({ period: 'NEXT_MONTH', page: 0, size: 100 });
const EDIT_UNAVAILABLE_MESSAGE = 'Пари можно редактировать только создателю, OWNER или ADMIN комнаты, пока событие не началось и пари открыто.';
const INLINE_EVENT_ERROR_KEYS = [
  'inlineEventGlobal',
  'inlineEventTitle',
  'inlineEventStartDate',
  'inlineEventEndDate',
  'inlineEventCategory',
  'inlineEventCustomCategory',
  'inlineEventOutcomes',
  'inlineEventNewOutcome',
];
const POPULAR_COMPETITIONS = [
  { code: 'PL', name: 'Англия' },
  { code: 'CL', name: 'Еврокубки' },
  { code: 'BL1', name: 'Германия' },
  { code: 'SA', name: 'Италия' },
  { code: 'PD', name: 'Испания' },
];

const buildRoomPagePath = (roomId) => `/rooms/${roomId}`;

const buildChallengeListPath = (roomId, leagueId) => (
  leagueId ? `/rooms/${roomId}/leagues/${leagueId}/challenges` : `/rooms/${roomId}/challenges`
);

const buildChallengeViewPath = (roomId, leagueId, challengeId) => (
  `${buildChallengeListPath(roomId, leagueId)}/${challengeId}`
);

const buildChallengeReturnPath = (roomId, leagueId) => (
  leagueId ? buildChallengeListPath(roomId, leagueId) : buildRoomPagePath(roomId)
);

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleString('ru-RU', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatEventDate = (event) => {
  const start = formatDate(event?.startDate || event?.eventDate || event?.date);
  const end = formatDate(event?.endDate);
  if (start && end) return `${start} — ${end}`;
  return start || end;
};

const isFutureEvent = isFutureEventHelper;

const normalizeCompetitionCode = (value) => value.trim().toUpperCase();

const mergeEventIntoList = (events, event) => {
  if (!event?.id) return events;
  return [event, ...events.filter((item) => String(item.id) !== String(event.id))];
};

const clearInlineEventErrors = (errors) => {
  const nextErrors = { ...errors };
  INLINE_EVENT_ERROR_KEYS.forEach((key) => delete nextErrors[key]);
  return nextErrors;
};

export default function CreateChallengePage() {
  const { roomId, leagueId, challengeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const isEditMode = Boolean(challengeId);
  const isLeagueScope = Boolean(leagueId);
  const challengePrefill = useMemo(() => readChallengePrefillSearch(location.search), [location.search]);
  const preselectedExternalEventId = !isEditMode ? challengePrefill.externalEventId : '';
  const preselectedEventId = !isEditMode && !preselectedExternalEventId ? challengePrefill.eventId : '';
  const preselectedExternalCompetition = challengePrefill.competition || DEFAULT_EXTERNAL_COMPETITION;

  const [room, setRoom] = useState(null);
  const [events, setEvents] = useState([]);
  const [leagueScoringProfile, setLeagueScoringProfile] = useState(null);
  const [eventSource, setEventSource] = useState(EVENT_SOURCE_LOCAL);
  const [eventId, setEventId] = useState('');
  const [inlineEventOpen, setInlineEventOpen] = useState(false);
  const [inlineEventTitle, setInlineEventTitle] = useState('');
  const [inlineEventDescription, setInlineEventDescription] = useState('');
  const [inlineEventStartDate, setInlineEventStartDate] = useState('');
  const [inlineEventEndDate, setInlineEventEndDate] = useState('');
  const [inlineEventCategory, setInlineEventCategory] = useState(EVENT_CATEGORY_DEFAULT);
  const [inlineEventCustomCategory, setInlineEventCustomCategory] = useState('');
  const [inlineEventOutcomes, setInlineEventOutcomes] = useState([]);
  const [inlineEventNewOutcome, setInlineEventNewOutcome] = useState('');
  const [inlineEventTypeId, setInlineEventTypeId] = useState('');
  const [inlineEventType, setInlineEventType] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [inlineEventLoading, setInlineEventLoading] = useState(false);
  const [externalCompetition, setExternalCompetition] = useState(DEFAULT_EXTERNAL_COMPETITION);
  const [externalEvents, setExternalEvents] = useState([]);
  const [externalEventId, setExternalEventId] = useState('');
  const [externalEventIds, setExternalEventIds] = useState([]);
  const [externalSearchPerformed, setExternalSearchPerformed] = useState(false);
  const [externalEventsLoading, setExternalEventsLoading] = useState(false);
  const [betDeadline, setBetDeadline] = useState('');
  const [scoringProfile, setScoringProfile] = useState(DEFAULT_SCORING_PROFILE);
  const [hideBetsUntilOwn, setHideBetsUntilOwn] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [leagueLoading, setLeagueLoading] = useState(Boolean(leagueId));
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [notEditable, setNotEditable] = useState(false);

  const targetLeagueIdForNavigation = isLeagueScope ? leagueId : null;
  const backPath = isEditMode
    ? buildChallengeViewPath(roomId, targetLeagueIdForNavigation, challengeId)
    : buildChallengeReturnPath(roomId, isLeagueScope ? leagueId : null);
  const scopeLabel = isLeagueScope ? 'Пари' : 'Комната';
const pageTitle = isEditMode ? 'Редактировать пари' : 'Создать пари';
  const roomLabel = room?.name || 'Комната';
  const breadcrumbItems = [
    { label: 'Комнаты', to: '/rooms' },
    { label: roomLabel, to: buildRoomPagePath(roomId) },
    ...(isLeagueScope || isEditMode ? [{ label: scopeLabel, to: backPath }] : []),
    { label: pageTitle },
  ];

  const selectedEvent = useMemo(
    () => events.find((event) => String(event.id) === String(eventId)),
    [events, eventId],
  );

  const selectedExternalEvent = useMemo(
    () => externalEvents.find((event) => String(event.externalId) === String(externalEventId)),
    [externalEvents, externalEventId],
  );

  const selectedChallengeEvent = eventSource === EVENT_SOURCE_EXTERNAL ? selectedExternalEvent : selectedEvent;


  useEffect(() => {
    let cancelled = false;
    setRoom(null);

    getRoom(roomId)
      .then((roomData) => {
        if (!cancelled) {
          setRoom(roomData);
          if (!isEditMode) {
            setScoringProfile(normalizeScoringProfile(roomData?.scoringProfile));
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setErrors((prev) => ({
            ...prev,
            global: prev.global || accessAwareMessage(err, 'Не удалось загрузить комнату'),
          }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [roomId, isEditMode]);

  useEffect(() => {
    if (isEditMode) return;
    setScoringProfile(normalizeScoringProfile(leagueScoringProfile || room?.scoringProfile));
    setErrors((prev) => ({ ...prev, [SCORING_PROFILE_ERROR_KEY]: null }));
  }, [isEditMode, room, leagueScoringProfile]);

  const availableEvents = useMemo(
    () => events.filter((event) => isFutureEvent(event) || String(event.id) === String(eventId)),
    [events, eventId],
  );

  const availableExternalEvents = useMemo(
    () => externalEvents.filter((event) => isFutureEvent(event) || String(event.externalId) === String(externalEventId)),
    [externalEvents, externalEventId],
  );

  useEffect(() => {
    document.title = `BetMate — ${pageTitle}`;
    setErrors({});
    setEventsLoading(true);
    getEvents(EVENT_LIST_FILTERS)
      .then(async (data) => {
        const loadedEvents = Array.isArray(data?.content) ? data.content : [];
        if (!preselectedEventId) {
          setEvents(loadedEvents);
          return;
        }

        setEventSource(EVENT_SOURCE_LOCAL);
        setEventId(preselectedEventId);
        if (loadedEvents.some((event) => String(event.id) === String(preselectedEventId))) {
          setEvents(loadedEvents);
          return;
        }

        try {
          const preselectedEvent = await getEvent(preselectedEventId);
          setEvents(mergeEventIntoList(loadedEvents, preselectedEvent));
        } catch (err) {
          setEvents(loadedEvents);
          setErrors((prev) => ({ ...prev, eventId: 'Не удалось загрузить выбранное событие' }));
        }
      })
      .catch(() => setErrors((prev) => ({ ...prev, global: 'Не удалось загрузить список событий' })))
      .finally(() => setEventsLoading(false));

    if (isLeagueScope) {
      setLeagueLoading(true);
      getLeague(roomId, leagueId)
        .then((leagueData) => setLeagueScoringProfile(leagueData?.scoringProfile || null))
        .catch((err) => setErrors((prev) => ({ ...prev, global: accessAwareMessage(err, 'Не удалось загрузить настройки пари') })))
        .finally(() => setLeagueLoading(false));
    } else {
      setLeagueScoringProfile(null);
      setLeagueLoading(false);
    }

    if (!isEditMode) {
      setInitialLoading(false);
      return;
    }

    setInitialLoading(true);
    getChallenge(roomId, leagueId, challengeId)
      .then((challenge) => {
        if (!challenge.editable || !challenge.managementAvailable) {
          setNotEditable(true);
          return;
        }
        setEventId(challenge.eventId ? String(challenge.eventId) : '');
        setBetDeadline(toDateTimeLocalValue(challenge.betDeadline));
        setHideBetsUntilOwn(Boolean(challenge.hideBetsUntilOwn));
        setScoringProfile(normalizeScoringProfile(challenge.scoringProfile || {
          drawPolicy: challenge.drawPolicy,
          aggregationStrategy: challenge.scoringAggregationStrategy,
          rules: challenge.scoringRules,
        }));
      })
      .catch((err) => accessAwareMessage(err, 'Не удалось загрузить пари для редактирования'))
      .finally(() => setInitialLoading(false));
  }, [roomId, leagueId, challengeId, isEditMode, isLeagueScope, pageTitle, preselectedEventId]);

  useEffect(() => {
    if (!preselectedExternalEventId) return;

    setEventSource(EVENT_SOURCE_EXTERNAL);
    setExternalCompetition(preselectedExternalCompetition);
    setExternalEventId(preselectedExternalEventId);
    setExternalSearchPerformed(true);
    setExternalEventsLoading(true);
    setErrors((prev) => ({ ...prev, externalEventId: null, global: null }));
    searchExternalEvents(preselectedExternalCompetition)
      .then((data) => {
        const loadedExternalEvents = Array.isArray(data) ? data : [];
        setExternalEvents(loadedExternalEvents);
        if (!loadedExternalEvents.some((event) => String(event.externalId) === String(preselectedExternalEventId))) {
          setErrors((prev) => ({ ...prev, externalEventId: 'Выбранный внешний матч не найден в ответе API' }));
        }
      })
      .catch((err) => {
        setExternalEvents([]);
        setErrors((prev) => ({ ...prev, externalEventId: getExternalSearchError(err) }));
      })
      .finally(() => setExternalEventsLoading(false));
  }, [preselectedExternalCompetition, preselectedExternalEventId]);

  // Load event types for inline event form
  useEffect(() => {
    listAllEventTypes()
      .then((types) => setEventTypes(Array.isArray(types) ? types : []))
      .catch(() => {/* non-critical */});
  }, []);

  // When inline EventType changes, resolve it and auto-generate outcomes
  useEffect(() => {
    if (!inlineEventTypeId) {
      setInlineEventType(null);
      return;
    }
    const found = eventTypes.find((et) => String(et.id) === String(inlineEventTypeId));
    setInlineEventType(found || null);
  }, [inlineEventTypeId, eventTypes]);

  useEffect(() => {
    if (!inlineEventType) return;
    if (inlineEventType.outcomeScheme === OutcomeScheme.CUSTOM) {
      setInlineEventOutcomes([]);
    } else {
      const generated = generateOutcomesFromEventType(inlineEventType);
      if (generated.length > 0) setInlineEventOutcomes(generated);
    }
    setErrors((prev) => ({ ...prev, inlineEventOutcomes: undefined }));
  }, [inlineEventType]);

  const inlineEventTypeOptions = useMemo(() => [
    { value: '', label: 'Без типа (выбрать категорию вручную)' },
    ...eventTypes.map((et) => ({
      value: String(et.id),
      label: `${outcomeSchemeIcon(et.outcomeScheme)} ${et.name}`,
    })),
  ], [eventTypes]);

  const eventOptions = availableEvents.map((event) => ({
    value: String(event.id),
    label: event.title || `Событие #${event.id}`,
    hint: formatEventDate(event),
  }));

  const externalEventOptions = availableExternalEvents.map((event) => ({
    value: String(event.externalId),
    label: event.title || event.externalId,
    hint: formatEventDate(event),
  }));


  const handleExternalSearch = async () => {
    const competition = normalizeCompetitionCode(externalCompetition);
    if (!competition) {
      setErrors((prev) => ({ ...prev, externalCompetition: 'Укажи код турнира' }));
      return;
    }

    setExternalCompetition(competition);
    setExternalEventsLoading(true);
    setExternalSearchPerformed(true);
    setExternalEventId('');
    setErrors((prev) => ({
      ...prev,
      externalCompetition: null,
      externalEventId: null,
      global: null,
    }));
    try {
      const data = await searchExternalEvents(competition);
      setExternalEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      setExternalEvents([]);
      setErrors((prev) => ({ ...prev, externalEventId: getExternalSearchError(err) }));
    } finally {
      setExternalEventsLoading(false);
    }
  };

  const handleExternalCompetitionKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    handleExternalSearch();
  };

  const handleEventSourceChange = (source) => {
    setEventSource(source);
    setErrors((prev) => ({
      ...prev,
      eventId: null,
      externalEventId: null,
      betDeadline: null,
    }));
  };

  const handleInlineEventAddOutcome = () => {
    const outcome = inlineEventNewOutcome.trim();
    if (!outcome) return;
    if (inlineEventOutcomes.includes(outcome)) {
      setErrors((prev) => ({ ...prev, inlineEventNewOutcome: 'Такой исход уже добавлен' }));
      return;
    }
    setInlineEventOutcomes((prev) => [...prev, outcome]);
    setInlineEventNewOutcome('');
    setErrors((prev) => ({ ...prev, inlineEventNewOutcome: null, inlineEventOutcomes: null }));
  };

  const handleInlineEventOutcomeKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    handleInlineEventAddOutcome();
  };

  const handleInlineEventRemoveOutcome = (outcome) => {
    setInlineEventOutcomes((prev) => prev.filter((item) => item !== outcome));
  };

  const handleInlineEventSubmit = async () => {
    const validationErrors = validateInlineEventForm();
    if (Object.keys(validationErrors).length) {
      setErrors((prev) => ({ ...clearInlineEventErrors(prev), ...validationErrors }));
      return;
    }

    setInlineEventLoading(true);
    setErrors((prev) => clearInlineEventErrors(prev));
    try {
      const createdEvent = await createEvent(buildInlineEventPayload());
      setEvents((prev) => mergeEventIntoList(prev, createdEvent));
      setEventSource(EVENT_SOURCE_LOCAL);
      setEventId(String(createdEvent.id));
      resetInlineEventForm();
      setInlineEventOpen(false);
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
inlineEventGlobal: err?.response?.data?.message || 'Не удалось создать событие прямо из формы пари',
      }));
    } finally {
      setInlineEventLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    const targetLeagueId = getTargetLeagueId();
    setLoading(true);
    setErrors({});
    try {
      if (eventSource === EVENT_SOURCE_EXTERNAL && externalEventIds.length > 1) {
        // Multi-select: create one challenge per selected match, betDeadline = event.startDate
        const overrides = buildMultiChallengeOverrides(externalEventIds, externalEvents);
        for (const perEvent of overrides) {
          const multiPayload = {
            ...buildPayload(targetLeagueId),
            ...perEvent,
          };
          await createChallenge(roomId, targetLeagueId, multiPayload);
        }
        navigate(buildChallengeReturnPath(roomId, targetLeagueId));
      } else if (isEditMode) {
        await updateChallenge(roomId, targetLeagueId, challengeId, buildPayload(targetLeagueId));
        navigate(buildChallengeViewPath(roomId, targetLeagueId, challengeId));
      } else {
        await createChallenge(roomId, targetLeagueId, buildPayload(targetLeagueId));
        navigate(buildChallengeReturnPath(roomId, targetLeagueId));
      }
    } catch (err) {
      setErrors({ global: getSubmitError(err) });
    } finally {
      setLoading(false);
    }
  };

  // ===================================================================================================================
  // = Implementation
  // ===================================================================================================================

  const validateForm = () => {
    const validationErrors = {};
    if (eventSource === EVENT_SOURCE_EXTERNAL) {
      const externalErrors = validateExternalEventSelection(
        externalEventIds, externalEventId, selectedExternalEvent, externalEvents
      );
      if (externalErrors.externalEventId) {
        validationErrors.externalEventId = externalErrors.externalEventId;
      }
    } else if (!eventId) {
      validationErrors.eventId = 'Выберите событие';
    } else if (!selectedEvent) {
      validationErrors.eventId = 'Выбранное событие не найдено';
    } else if (selectedEvent && !isFutureEvent(selectedEvent)) {
      validationErrors.eventId = 'Выберите событие, которое ещё не началось';
    }
    const scoringProfileError = validateScoringRules(scoringProfile.rules);
    if (scoringProfileError) validationErrors[SCORING_PROFILE_ERROR_KEY] = scoringProfileError;
    if (betDeadline && selectedChallengeEvent && isBetDeadlineAfterEventStart()) {
      validationErrors.betDeadline = 'Дедлайн ставок не может быть позже начала события';
    }
    // Multi-select external events: betDeadline is auto-set per event, skip manual validation
    return validationErrors;
  };

  const validateInlineEventForm = () => {
    const validationErrors = {};
    if (!inlineEventTitle.trim()) validationErrors.inlineEventTitle = 'Название обязательно';
    if (!inlineEventStartDate) validationErrors.inlineEventStartDate = 'Дата начала обязательна';
    if (!inlineEventEndDate) validationErrors.inlineEventEndDate = 'Дата окончания обязательна';
    if (inlineEventStartDate && new Date(inlineEventStartDate).getTime() <= Date.now()) {
      validationErrors.inlineEventStartDate = 'Дата начала должна быть в будущем';
    }
    if (inlineEventStartDate && inlineEventEndDate && new Date(inlineEventEndDate) <= new Date(inlineEventStartDate)) {
      validationErrors.inlineEventEndDate = 'Дата окончания должна быть позже даты начала';
    }
    if (!inlineEventTypeId && inlineEventCategory === EVENT_CATEGORY_OTHER && !inlineEventCustomCategory.trim()) {
      validationErrors.inlineEventCustomCategory = 'Укажи свою категорию';
    }
    if (inlineEventOutcomes.length === 0 && !inlineEventTypeId) validationErrors.inlineEventOutcomes = 'Добавьте хотя бы один исход';
    return validationErrors;
  };

  const buildInlineEventPayload = () => {
    const payload = {
      title: inlineEventTitle.trim(),
      description: inlineEventDescription.trim(),
      startDate: toUserOffsetDateTime(inlineEventStartDate),
      endDate: toUserOffsetDateTime(inlineEventEndDate),
      category: inlineEventCategory,
      customCategory: inlineEventCategory === EVENT_CATEGORY_OTHER ? inlineEventCustomCategory.trim() : null,
      outcomes: inlineEventOutcomes,
    };
    if (inlineEventTypeId) {
      payload.eventTypeId = Number(inlineEventTypeId);
      payload.drawPolicy = inlineEventType?.scoreDrawPolicy || null;
    }
    return payload;
  };

  const resetInlineEventForm = () => {
    setInlineEventTitle('');
    setInlineEventDescription('');
    setInlineEventStartDate('');
    setInlineEventEndDate('');
    setInlineEventCategory(EVENT_CATEGORY_DEFAULT);
    setInlineEventCustomCategory('');
    setInlineEventOutcomes([]);
    setInlineEventNewOutcome('');
    setInlineEventTypeId('');
    setInlineEventType(null);
  };

  const isBetDeadlineAfterEventStart = () => {
    const eventStart = selectedChallengeEvent?.startDate || selectedChallengeEvent?.eventDate || selectedChallengeEvent?.date;
    if (!eventStart || !betDeadline) return false;
    // Normalise both values to UTC instants for correct comparison.
    // betDeadline is a datetime-local string (no offset) — treat as user's local time.
    // eventStart may arrive with or without offset; toDateTimeLocalValue + toUserOffsetDateTime
    // ensures consistent interpretation as the user's local timezone.
    const deadlineInstant = new Date(toUserOffsetDateTime(betDeadline)).getTime();
    const eventStartInstant = new Date(eventStart).getTime();
    return deadlineInstant > eventStartInstant;
  };

  const getTargetLeagueId = () => {
    if (isLeagueScope) return leagueId;
    return null;
  };


  const buildPayload = (targetLeagueId, overrides = {}) => ({
    eventId: eventSource === EVENT_SOURCE_LOCAL ? Number(eventId) : null,
    externalEventId: eventSource === EVENT_SOURCE_EXTERNAL ? externalEventId : null,
    leagueId: targetLeagueId ? Number(targetLeagueId) : null,
    betDeadline: toUserOffsetDateTime(betDeadline),
    hideBetsUntilOwn,
    scoringProfile: toScoringProfilePayload(scoringProfile),
    scoringRules: buildScoringRules(),
    drawPolicy: scoringProfile.drawPolicy,
    scoringAggregationStrategy: scoringProfile.aggregationStrategy,
    ...overrides,
  });

  const buildScoringRules = () => toScoringRulesMap(scoringProfile.rules);

  const getSubmitError = (err) => (
    err?.response?.data?.message
    || accessAwareMessage(err, `Не удалось ${isEditMode ? 'сохранить' : 'создать'} пари. Попробуй ещё раз.`)
  );

  const getExternalSearchError = (err) => (
    err?.response?.data?.message
    || accessAwareMessage(err, 'Не удалось загрузить футбольные события. Проверь код турнира и попробуй ещё раз.')
  );

  const isSubmitDisabled = () => {
    if (loading || eventsLoading || leagueLoading || externalEventsLoading || inlineEventLoading) return true;
    if (isEditMode || eventSource === EVENT_SOURCE_LOCAL) return availableEvents.length === 0;
    const hasExternalSelection = externalEventIds.length > 0 || externalEventId;
    return availableExternalEvents.length === 0 || !hasExternalSelection;
  };

  // Note: isExternalSubmitDisabled is available from challengeCreationHelpers for testing

  if (initialLoading) {
    return (
      <div className="bm-animate-in" style={{ maxWidth: 560 }}>
        <SkeletonCard />
      </div>
    );
  }

  if (notEditable) {
    return (
      <EmptyState
        icon={<PiWarningDuotone size={40} />}
        title="Редактирование недоступно"
        description={EDIT_UNAVAILABLE_MESSAGE}
        action={
          <Button
            variant="ghost"
            icon={<PiArrowLeftDuotone size={16} />}
            onClick={() => navigate(buildChallengeViewPath(roomId, leagueId, challengeId))}
          >
К пари
          </Button>
        }
      />
    );
  }

  return (
    <div className="bm-animate-in" style={{ maxWidth: 560 }}>
      <Breadcrumb items={breadcrumbItems} />

      <h1 className="bm-h2" style={{ marginTop: 'var(--bm-space-lg)', marginBottom: 'var(--bm-space-xl)' }}>
        {pageTitle}
      </h1>

      <Card>
        {errors.global && (
          <div className="bm-auth-error" style={{ marginBottom: 'var(--bm-space-md)' }}>
            {errors.global}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-md)' }}
        >
          {!isEditMode && (
            <div>
              <div className="bm-label" style={{ marginBottom: 6 }}>Источник события</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <Button
                  variant={eventSource === EVENT_SOURCE_LOCAL ? 'primary' : 'secondary'}
                  onClick={() => handleEventSourceChange(EVENT_SOURCE_LOCAL)}
                >
                  Мои события
                </Button>
                <Button
                  variant={eventSource === EVENT_SOURCE_EXTERNAL ? 'primary' : 'secondary'}
                  onClick={() => handleEventSourceChange(EVENT_SOURCE_EXTERNAL)}
                >
                  Футбольный API
                </Button>
              </div>
            </div>
          )}

          {eventSource === EVENT_SOURCE_LOCAL || isEditMode ? (
            eventsLoading ? (
              <div>
                <div className="bm-label" style={{ marginBottom: 6 }}>Событие *</div>
                <SkeletonText lines={1} />
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-sm)' }}>
                <Select
                  label="Событие *"
                  placeholder="— Выберите событие —"
                  value={eventId}
                  onChange={setEventId}
                  options={eventOptions}
                  error={errors.eventId}
                />

                {selectedEvent && (
                  <Card variant="glass" style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '12px 14px' }}>
                    <div className="bm-font-medium">{selectedEvent.title || `Событие #${selectedEvent.id}`}</div>
                    <p className="bm-text-secondary bm-text-sm">
                      {formatEventDate(selectedEvent) || 'Даты не указаны'}
                    </p>
                    <p className="bm-text-secondary bm-text-sm">
                      Событие уже выбрано. Чтобы заменить его, выбери другое в списке выше.
                    </p>
                  </Card>
                )}

                {!isEditMode && (
                  <Button
                    variant={inlineEventOpen ? 'secondary' : 'primary'}
                    size="sm"
                    icon={<PiCalendarDuotone size={14} />}
                    onClick={() => setInlineEventOpen((prev) => !prev)}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    {inlineEventOpen ? 'Скрыть создание события' : 'Создать новое событие здесь'}
                  </Button>
                )}

                {!isEditMode && inlineEventOpen && (
                  <Card className="bm-inline-event-card">
                    <div className="bm-inline-event-card__header">
                      <span className="bm-inline-event-card__icon">
                        <PiCalendarDuotone size={20} />
                      </span>
                      <div>
                        <div className="bm-inline-event-card__eyebrow">Быстрое создание</div>
                        <div className="bm-font-medium">Новое событие</div>
                        <p className="bm-text-secondary bm-text-sm bm-inline-event-card__hint">
После сохранения оно автоматически подставится в пари.
                        </p>
                      </div>
                    </div>

                    {errors.inlineEventGlobal && <div className="bm-auth-error">{errors.inlineEventGlobal}</div>}

                    <Select
                      label="Тип события"
                      value={inlineEventTypeId}
                      onChange={(value) => {
                        setInlineEventTypeId(value);
                        setErrors((prev) => ({ ...prev, inlineEventOutcomes: null }));
                      }}
                      options={inlineEventTypeOptions}
                    />
                    {inlineEventType && inlineEventType.outcomeScheme !== OutcomeScheme.CUSTOM && (
                      <div className="bm-text-secondary bm-text-sm" style={{ padding: '0 var(--bm-space-xs)' }}>
                        Автоматические исходы: {inlineEventOutcomes.join(', ') || '—'}
                      </div>
                    )}

                    <Input
                      label="Название события *"
                      type="text"
                      value={inlineEventTitle}
                      onChange={(e) => setInlineEventTitle(e.target.value)}
                      placeholder="Например: Финал турнира"
                      error={errors.inlineEventTitle}
                    />
                    <Input
                      label="Описание"
                      as="textarea"
                      value={inlineEventDescription}
                      onChange={(e) => setInlineEventDescription(e.target.value)}
                      placeholder="Краткое описание события"
                      rows={2}
                    />
                    {!inlineEventType && (
                      <Select
                        label="Категория *"
                        value={inlineEventCategory}
                        onChange={(value) => {
                          setInlineEventCategory(value);
                          setErrors((prev) => ({ ...prev, inlineEventCustomCategory: null }));
                        }}
                        options={EVENT_CATEGORY_OPTIONS}
                        error={errors.inlineEventCategory}
                      />
                    )}
                    {!inlineEventType && inlineEventCategory === EVENT_CATEGORY_OTHER && (
                      <Input
                        label="Своя категория *"
                        type="text"
                        value={inlineEventCustomCategory}
                        onChange={(e) => setInlineEventCustomCategory(e.target.value)}
                        placeholder="Например: Формула-1"
                        error={errors.inlineEventCustomCategory}
                      />
                    )}
                    <div className="bm-inline-event-card__dates">
                      <DateTimePicker
                        label="Дата начала *"
                        value={inlineEventStartDate}
                        onChange={setInlineEventStartDate}
                        error={errors.inlineEventStartDate}
                      />
                      <DateTimePicker
                        label="Дата окончания *"
                        value={inlineEventEndDate}
                        onChange={setInlineEventEndDate}
                        error={errors.inlineEventEndDate}
                      />
                    </div>

                    {(!inlineEventType || inlineEventType.outcomeScheme === OutcomeScheme.CUSTOM) && (
                    <div>
                      <label className="bm-label bm-inline-event-card__outcomes-label">
                        <PiListBulletsDuotone size={16} />
                        Исходы события {!inlineEventType ? '*' : ''}
                      </label>
                      <div className="bm-inline-event-card__outcome-input">
                        <Input
                          type="text"
                          value={inlineEventNewOutcome}
                          onChange={(e) => setInlineEventNewOutcome(e.target.value)}
                          onKeyDown={handleInlineEventOutcomeKeyDown}
                          placeholder="Например: Победа команды А"
                          error={errors.inlineEventNewOutcome}
                          style={{ flex: 1 }}
                        />
                        <Button
                          variant="secondary"
                          size="md"
                          onClick={handleInlineEventAddOutcome}
                          type="button"
                        >
                          Добавить
                        </Button>
                      </div>

                      {errors.inlineEventOutcomes && (
                        <span className="bm-input-error-msg">{errors.inlineEventOutcomes}</span>
                      )}

                      {inlineEventOutcomes.length > 0 && (
                        <div className="bm-inline-event-card__outcome-list">
                          {inlineEventOutcomes.map((outcome) => (
                            <div className="bm-inline-event-card__outcome-item" key={outcome}>
                              <span className="bm-text-secondary bm-text-sm">{outcome}</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                icon={<PiTrashDuotone size={14} />}
                                onClick={() => handleInlineEventRemoveOutcome(outcome)}
                                type="button"
                                style={{ color: 'var(--bm-accent-red)', padding: '4px 8px' }}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    )}

                    <Button
                      className="bm-inline-event-card__submit"
                      variant="primary"
                      type="button"
                      loading={inlineEventLoading}
                      onClick={handleInlineEventSubmit}
                    >
                      Создать событие и выбрать его
                    </Button>
                  </Card>
                )}
              </div>
            )
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-sm)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'end' }}>
                <Input
                  label="Код турнира football-data.org"
                  value={externalCompetition}
                  onChange={(e) => setExternalCompetition(e.target.value.toUpperCase())}
                  onKeyDown={handleExternalCompetitionKeyDown}
                  placeholder="PL, CL, BL1..."
                  error={errors.externalCompetition}
                />
                <Button
                  variant="secondary"
                  loading={externalEventsLoading}
                  icon={<PiMagnifyingGlassDuotone size={16} />}
                  onClick={handleExternalSearch}
                  style={{ marginBottom: errors.externalCompetition ? 22 : 0 }}
                >
                  Найти
                </Button>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {POPULAR_COMPETITIONS.map((competition) => (
                  <Button
                    key={competition.code}
                    variant="ghost"
                    size="sm"
                    onClick={() => setExternalCompetition(competition.code)}
                  >
                    {competition.code} · {competition.name}
                  </Button>
                ))}
              </div>

              {externalEventsLoading ? (
                <div>
                  <div className="bm-label" style={{ marginBottom: 6 }}>Матчи *</div>
                  <SkeletonText lines={1} />
                </div>
              ) : (
                <Select
                  label="Матчи *"
                  placeholder={externalSearchPerformed ? '— Выберите матчи —' : 'Сначала нажми «Найти»'}
                  value={externalEventIds.length > 0 ? externalEventIds : (externalEventId || '')}
                  onChange={(val) => {
                    if (Array.isArray(val)) {
                      setExternalEventIds(val);
                      setExternalEventId(val.length === 1 ? val[0] : '');
                      if (val.length > 1) setExternalEventId('');
                    } else {
                      setExternalEventId(val);
                      setExternalEventIds(val ? [val] : []);
                    }
                  }}
                  options={externalEventOptions}
                  error={errors.externalEventId}
                  disabled={!externalSearchPerformed}
                  multiple
                />
              )}

              {externalSearchPerformed && !externalEventsLoading && availableExternalEvents.length === 0 && !errors.externalEventId && (
                <p className="bm-text-secondary bm-text-sm">
                  Для этого турнира нет будущих матчей в ответе API. Попробуй другой код соревнования.
                </p>
              )}
            </div>
          )}

          {shouldHideBetDeadlineInput(eventSource, externalEventIds.length) ? (
            <p className="bm-text-secondary bm-text-sm">
              Дедлайн ставок автоматически устанавливается по дате начала каждого матча.
            </p>
          ) : (
            <>
              <DateTimePicker
                label="Дедлайн ставок"
                value={betDeadline}
                onChange={setBetDeadline}
                error={errors.betDeadline}
              />
              <p className="bm-text-secondary bm-text-sm">
                Если дедлайн не указан, backend выставит его не позже старта события.
              </p>
            </>
          )}


          <ScoringProfileBuilder
            value={scoringProfile}
            onChange={setScoringProfile}
title={isEditMode ? 'Снапшот правил пари' : 'Предпросмотр снапшота правил'}
description="Эти правила будут зафиксированы в снапшоте пари после сохранения."
            error={errors[SCORING_PROFILE_ERROR_KEY]}
          />

          <label
            className="bm-text-sm"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 14px',
              border: hideBetsUntilOwn ? '1px solid rgba(108,99,255,0.32)' : '1px solid var(--bm-border)',
              borderRadius: 'var(--bm-radius-lg)',
              background: hideBetsUntilOwn ? 'rgba(108,99,255,0.06)' : 'var(--bm-bg-elevated)',
              cursor: 'pointer',
              transition: 'border-color 160ms ease, background 160ms ease, box-shadow 160ms ease',
              boxShadow: hideBetsUntilOwn ? '0 0 0 3px rgba(108,99,255,0.06)' : 'none',
            }}
          >
            <input
              type="checkbox"
              checked={hideBetsUntilOwn}
              onChange={(e) => setHideBetsUntilOwn(e.target.checked)}
              style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
            />
            <span
              aria-hidden="true"
              style={{
                width: 42,
                height: 24,
                padding: 2,
                border: hideBetsUntilOwn ? '1px solid var(--bm-accent-primary)' : '1px solid rgba(255,255,255,0.34)',
                borderRadius: 999,
                background: hideBetsUntilOwn ? 'var(--bm-accent-primary)' : 'rgba(255,255,255,0.16)',
                transition: 'background 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
                boxShadow: hideBetsUntilOwn ? '0 0 0 3px rgba(108,99,255,0.12)' : 'inset 0 0 0 1px rgba(0,0,0,0.18)',
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  display: 'block',
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: '#ffffff',
                  transform: hideBetsUntilOwn ? 'translateX(18px)' : 'translateX(0)',
                  transition: 'transform 160ms ease',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.36)',
                }}
              />
            </span>
            <span
              style={{
                width: 34,
                height: 34,
                borderRadius: 'var(--bm-radius-sm)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: hideBetsUntilOwn ? 'var(--bm-accent-primary)' : 'var(--bm-text-secondary)',
                background: hideBetsUntilOwn ? 'rgba(108,99,255,0.1)' : 'rgba(255,255,255,0.03)',
                flexShrink: 0,
              }}
            >
              <PiEyeSlashDuotone size={18} />
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span className="bm-font-medium">Скрывать значения чужих ставок</span>
              <br />
              <span className="bm-text-secondary">
                Видно, кто уже поставил; исход и сумма откроются после твоей ставки.
              </span>
            </span>
          </label>

          {(eventSource === EVENT_SOURCE_LOCAL || isEditMode) && !eventsLoading && availableEvents.length === 0 && !errors.global && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 14px',
                background: 'rgba(255, 140, 66, 0.08)',
                border: '1px solid rgba(255, 140, 66, 0.2)',
                borderRadius: 'var(--bm-radius-md)',
              }}
            >
              <PiCalendarDuotone size={16} style={{ color: 'var(--bm-accent-orange)', flexShrink: 0 }} />
              <span className="bm-text-sm" style={{ color: 'var(--bm-accent-orange)' }}>
                Нет событий, которые ещё не начались. Создай событие прямо в этой форме или выбери матч через футбольный API.
              </span>
            </div>
          )}

          <div
            style={{
              display: 'flex',
              gap: 'var(--bm-space-sm)',
              justifyContent: 'flex-end',
              paddingTop: 'var(--bm-space-sm)',
            }}
          >
            <Button
              variant="ghost"
              type="button"
              onClick={() => navigate(backPath)}
            >
              Отмена
            </Button>
            <Button
              variant="primary"
              type="submit"
              loading={loading || eventsLoading || leagueLoading || externalEventsLoading}
              disabled={isSubmitDisabled()}
            >
              {isEditMode ? 'Сохранить' : getSubmitButtonLabel(false, eventSource, externalEventIds.length)}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
